import * as React from 'react';
import { SxProps, Theme } from '@mui/material';
import { List } from '../List';
import { RecursiveListItem, RecursiveListDataItem } from './RecursiveListItem';

export interface RecursiveListProps {
  inputRef?: React.MutableRefObject<any>;
  dataList: RecursiveListDataItem[];
  searchString?: string;
  disabled?: boolean;
  isSelectedList?: boolean;
  forceShowRecursion?: boolean;
  parentSelectable?: boolean;
  clickHandler?: (item: RecursiveListDataItem) => void;
  doubleClickHandler?: (item: RecursiveListDataItem) => void;
  expandChildClickHandler?: (isLoading?: boolean) => void;
  toggleCascadeHandler?: (item: RecursiveListDataItem) => void;
  toolTipExpand: string;
  toolTipCollapse: string;
  toolTipRemove: string;
  toolTipCannotRemove: string;
  textCascade?: string;
  sx?: SxProps<Theme>;
}

const returnProperlyFilteredList = (dataList: RecursiveListDataItem[], searchString: string, disabled: boolean) => {
  const tempList = dataList.filter((item: RecursiveListDataItem) => !searchString || item.label.toLowerCase().includes(searchString));
  const firstTabItem = tempList[0];
  const properlyTabFocusedList = tempList.map(item => {
    const newItem = { ...item };
    if (firstTabItem) {
      newItem.tabFocus = newItem.id === firstTabItem.id && !disabled;
    }
    return newItem;
  });
  return [...properlyTabFocusedList];
};

const styles: Record<string, SxProps<Theme>> = {
  listRoot: {
    '& > li': {
      padding: 0
    },
    paddingBottom: 2
  }
};

const RecursiveListBase = ({
  dataList,
  searchString,
  clickHandler,
  expandChildClickHandler,
  disabled,
  isSelectedList,
  forceShowRecursion,
  parentSelectable,
  doubleClickHandler,
  toggleCascadeHandler,
  toolTipExpand,
  toolTipCollapse,
  toolTipCannotRemove,
  toolTipRemove,
  inputRef,
  textCascade,
  sx
}: RecursiveListProps) => {
  const [filteredDataList, setFilteredDataList] = React.useState([...dataList]);
  const { listRoot } = styles;

  React.useEffect(() => {
    setFilteredDataList([...returnProperlyFilteredList(dataList, searchString || '', disabled || false)]);
  }, [dataList, searchString, disabled]);

  return (
    <List dense component="nav" role="list" sx={[listRoot, sx] as SxProps<Theme>} ref={inputRef}>
      {filteredDataList.map((dataListItem: RecursiveListDataItem) => (
        <RecursiveListItem
          id={dataListItem.id}
          selected={dataListItem.selected}
          expandable={dataListItem.expandable}
          dataListItem={dataListItem}
          {...(dataListItem.tabFocus && { tabFocus: true })}
          clickHandler={clickHandler}
          doubleClickHandler={doubleClickHandler}
          expandChildClickHandler={expandChildClickHandler}
          key={dataListItem.id}
          isSelectedList={isSelectedList}
          forceShowRecursion={forceShowRecursion}
          label={dataListItem.label}
          searchString={searchString}
          showCascade={dataListItem.showCascade}
          showInnerChildren={false}
          showChildren={dataListItem.showChildren}
          parentSelectable={parentSelectable}
          toggleCascadeHandler={toggleCascadeHandler}
          toolTipExpand={toolTipExpand}
          toolTipCollapse={toolTipCollapse}
          toolTipCannotRemove={toolTipCannotRemove}
          toolTipRemove={toolTipRemove}
          textCascade={textCascade}
        />
      ))}
    </List>
  );
};

RecursiveListBase.defaultProps = {
  isLoading: false
};

const RecursiveList = React.memo(RecursiveListBase);

export { RecursiveList };
